<template>
  <PivotFooterCell
    v-for="cell in rowCells"
    :key="cell.key"
    label
    :text="cell.text"
    :row
    :col="cell.col"
  />
  <PivotFooterCell
    v-for="cell in valueCells"
    :key="cell.key"
    :text="cell.text"
    :raw-value="cell.rawValue"
    :field="cell.field"
    :row
    :col="cell.col"
    :sign-colored="cell.signColored"
  />
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import { PivotTableData, PivotTableValueRenderers } from '@types'

import { GRAND_TOTAL_CAPTION, RENDERERS_PROVIDE_KEY, SIGN_COLORED_FIELDS_PROVIDE_KEY } from '@/components/DS/patterns/Pivot/utils/const'
import { getValuesByColumns, renderValue } from '@/components/DS/patterns/Pivot/utils/query'

import PivotFooterCell from './primitives/PivotFooterCell.vue'

type Props = {
  data: PivotTableData
  columns: PivotTableData[]
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
  /** This footer's own `data-row` — one past the last body row. */
  row: number
}

const { data, columns, colsList = [], rowsList = [], valuesList = [], row } = defineProps<Props>()

const renderers = inject(RENDERERS_PROVIDE_KEY, computed<PivotTableValueRenderers>(() => ({})))
const signColoredFields = inject(SIGN_COLORED_FIELDS_PROVIDE_KEY, [])

const rowCells = computed(() => rowsList.map((_, index) => ({
  key: `row:${index}`,
  text: index === 0 ? GRAND_TOTAL_CAPTION : '',
  col: index,
})))

const valueCells = computed(() => {
  const cells: { key: string, text: string, rawValue: unknown, field: string, signColored: boolean, col: number }[] = []
  const valuesByColumns = getValuesByColumns(data, columns, colsList, valuesList)
  let col = rowsList.length
  for (const { value, valueField, path } of valuesByColumns) {
    cells.push({ key: `col:${path.join('_')}`, text: renderValue(value, valueField, renderers.value), rawValue: value, field: valueField, signColored: signColoredFields.includes(valueField), col: col++ })
  }
  for (const field of valuesList) {
    cells.push({ key: `total:${field}`, text: renderValue(data[field], field, renderers.value), rawValue: data[field], field, signColored: signColoredFields.includes(field), col: col++ })
  }
  return cells
})
</script>
